/* eslint-disable */
import React, { useContext, useEffect, useRef, useState } from "react";
import yaml, { CORE_SCHEMA } from "js-yaml";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import { APPFACTOR, getApiUrl } from "./commons";
import { poolData } from "../UserPool";
import { UserContext } from "../context/user/UserContext";
import { DialogLogo } from "./APPF_ICONS";
import { MainBg } from "./MainBg.jsx";
import { MainBg2 } from "./MainBg-2.js";

import { ThemeContext } from "./theme-toggler/theme-context";
import { THEME_KEY } from "./theme-toggler/constant";


// Ask, then keep asking on a ramp. A freshly started orchestrator accepts the
// socket before it will answer, so the first attempt is often lost; spacing
// every attempt evenly meant a miss cost the full interval and recovery drifted
// to seven or ten seconds. Early attempts are cheap - catch it the moment it is
// ready - then settle into a slow poll.
const askOnRamp = (send, ramp = [200, 600, 1200, 2000], interval = 2500) => {
    const timers = ramp.map((delay) => setTimeout(send, delay));
    const poll = setInterval(send, interval);

    return () => {
        timers.forEach(clearTimeout);
        clearInterval(poll);
    };
};

// The password each password-check was sent to verify, by request id.
//
// A reply used to be answered by reading window._g.localSecret back, which says
// nothing about the request it belongs to. If a stored-password check outlived
// the 1200ms verifying hold and the user had since typed a *different* password,
// that check's success unlocked and persisted whatever happened to be on the
// global - a password no orchestrator had ever accepted.
//
// Ids are compared lower-cased: the orchestrator parses the id as a UUID and
// echoes it canonically, so a reply does not come back in the case we sent it.
const probedPasswords = new Map();
const MAX_TRACKED_PROBES = 8;

// Ids are generated lower-case and compared lower-case. The orchestrator parses
// the id as a UUID and re-serialises it canonically, so an id sent with any
// upper case in it does not come back as the string we sent - the reason the
// status check a few lines down sends "51A10055-..." and compares "51a10055-...".
// Generating them canonically is the fix; the comparison stays defensive.
const probeKey = (id) => `${id}`.toLowerCase();

// Keeps the 420005ec prefix both reply handlers route on, and fills the last
// UUID group with 48 random bits - the old tail was four digits from
// Math.random, which is 9000 values and mixed case.
export const newProbeId = () => {
    const bytes = new Uint8Array(6);

    crypto.getRandomValues(bytes);

    const tail = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

    return `420005ec-f00d-face-babe-${tail}`;
};

export const rememberProbe = (id, password) => {
    probedPasswords.set(probeKey(id), password);

    while (probedPasswords.size > MAX_TRACKED_PROBES) {
        probedPasswords.delete(probedPasswords.keys().next().value);
    }

    return id;
};

// Not consumed on read: this dialog and the page hosting it both handle the same
// reply, so the second one must not see it as "no password" and re-lock.
export const probedPassword = (id) => probedPasswords.get(probeKey(id)) || "";

/**
 * Publish a password the orchestrator has just accepted.
 *
 * window._g.localSecret is what the send path falls back to for every request
 * that is not itself a password check, so it still has to hold the live
 * password - but only ever one that has been verified. It used to be written
 * the moment a password was typed, and read back when a reply arrived, so a
 * reply could be answered by a password it had nothing to do with.
 */
export const holdVerifiedSecret = (password) => {
    try {
        window._g = window._g || {};
        window._g.localSecret = password;
    } catch (e) { /* */ }
};

/**
 * Forget the secret store password everywhere it is held.
 *
 * The three copies of this had already drifted - the one in this file skipped
 * the window._g reset, so a rejected password stayed on the global for the next
 * request to send.
 */
export const clearStoredSecret = () => {
    probedPasswords.clear();

    try { delete sessionStorage.ls; } catch (e) { /* */ }

    try {
        window._g = window._g || {};
        window._g.localSecret = "";
    } catch (e) { /* */ }
};

export const wPostMessage = (inPayload) => {
    window.postMessage(
        JSON.stringify({
            type: APPFACTOR.command,
            scanRaw: true,
            payload: inPayload
        })
    );    
}

export async function setTheConfig(url, poolData, hashicorpURLPrefix, namespace = "") {
    
    try {
        if (!poolData.accessToken || poolData.accessToken == "" ) {
            poolData.refreshToken();
        }

        let confJSON = {
            scanner_config:{
                api_url_prefix:`${url}`
            }
        };

        try{
            if(hashicorpURLPrefix){
                confJSON.scanner_config.hashicorp_vault = {
                    url_prefix: hashicorpURLPrefix,
                    "ignore_certificate_errors": true,
                    // "namespace": "..." 
                }
                if( namespace && namespace?.length > 0){
                    confJSON.scanner_config.hashicorp_vault.namespace = namespace;
                }
            }
        }catch(e){
            console.log(e);
        }

        let localSecret = sessionStorage.ls && sessionStorage.ls !== "" ? atob(sessionStorage.ls) : "" || ""

        let setConfig  = {
            "type": "set_scanner_config",
            "id": "cf121212-1212-1212-1212-121212121212",
            "secret_store_password": localSecret,
            ...confJSON
        };

    
        wPostMessage(setConfig);
    } catch (e) {
        /* */
        console.log("setTheConfig error", e)
    }
}

// Several screens render this component and they nest: ApplicationTable renders
// one, and the ModWizardV2 it mounts renders another. Because the Dialog is
// keepMounted, that put two of everything in the DOM - including two elements
// with id="lsPass" - so a click could land on the twin, the second overlay
// blocked the page, and validation read the wrong input. Only the
// first-mounted instance renders; the rest stand down and re-elect on unmount.
const mountedInstances = [];

const electPrimaryInstance = () => {
    mountedInstances.forEach((instance, index) => instance.setIsPrimary(index === 0));
};

export default function EnterSecretPassword (props) {
    const { theme } = useContext(ThemeContext);
    const isDarkMode = theme === THEME_KEY.DARK;

    const [isPrimaryInstance, setIsPrimaryInstance] = useState(false);

    useEffect(() => {
        const instance = { setIsPrimary: setIsPrimaryInstance };

        mountedInstances.push(instance);
        electPrimaryInstance();

        return () => {
            const index = mountedInstances.indexOf(instance);

            if (index !== -1) {
                mountedInstances.splice(index, 1);
            }

            electPrimaryInstance();
        };
    }, []);

    // Start locked even when sessionStorage already holds a password. It may be
    // a stale or rejected one, and seeding straight from it meant the app came up
    // "unlocked" with no prompt, only to fail on the first secret operation. The
    // stored value is a candidate: verifyStoredSecret below asks the orchestrator
    // whether it actually opens the store, and the reply decides.
    const [localSecret, setLocalSecret] = useState("");
    const [firstTimePassword, setFirstTimePassword] = useState(false);
    const [localSecretError, setLocalSecretError] = useState("");
    const [initDone,setInitDone] = useState(false);
    // True while a stored password is being checked on a fresh connection. The
    // dialog waits it out so a successful auto-unlock never flashes a prompt.
    const [verifying, setVerifying] = useState(false);
    // Stops the stored-password retry ramp below. Held in a ref because the reply
    // handler that needs to stop it lives in a different effect.
    const stopStoredProbeRef = useRef(null);

    const stopStoredProbe = () => {
        if (stopStoredProbeRef.current) {
            stopStoredProbeRef.current();
            stopStoredProbeRef.current = null;
        }
    };
    const [secretInitDone,setSecretInitDone] = useState(false);

    const [oConfig, setOConfig] = useState({
        config:sessionStorage?.oconfig || false,
        store:sessionStorage?.ostore || false
    });
     
    const { gdata } = useContext(UserContext);
    const lsPass = useRef();
    const lsPass2 = useRef();


    useEffect(() => {
        setTimeout(() => {
            try{
                let ls = lsPass.current;

                ls.focus();
                ls.select();
            }catch(e){ /* */ }
        }, 142);

        if (initDone) {
            // Store creation lives in validatePassword now, where it happens once
            // and is confirmed before anything unlocks. Creating it here as well
            // fired a second add_secret the moment localSecret was set - oConfig
            // still reported store:false at that point - and the orchestrator
            // answered "Secret already exists, use -f to overwrite".

            if(oConfig.config == false) {
                

                if (localSecret != "") {
                    setTheConfig(getApiUrl(poolData.envURL), poolData);
                }
            }

            setFirstTimePassword(!oConfig.store);
        }
    }, [oConfig,initDone,localSecret,secretInitDone]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key == "Enter") {
                if(typeof sessionStorage.ls === "undefined" || sessionStorage.ls == ""){
                    // firstTimePassword has to be read fresh. With an empty dependency
                    // list this closed over its mount-time value, which is always
                    // false - and this handler only runs when sessionStorage is
                    // empty, i.e. exactly on the first-time prompt. So Enter took the
                    // unlock branch there: it skipped the confirm-match check and
                    // sent get_secrets instead of the add_secret that creates the
                    // store, and setting an initial password by pressing Enter did
                    // nothing at all. It used to be survivable because an effect
                    // keyed on oConfig.store == false sent add_secret anyway; that
                    // duplicate was removed, so nothing covers this now.
                    validatePassword(firstTimePassword, lsPass.current, lsPass2.current);
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            // Same capture flag it was registered with, or the listener is never
            // removed and they accumulate on every re-render.
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    // eslint-disable-next-line
    }, [firstTimePassword]);

    // Ask the orchestrator whether a password already held in sessionStorage really
    // opens the store. Same check the dialog runs on a typed password, so the same
    // reply handler unlocks on success or clears it and prompts on failure.
    useEffect(() => {
        if (!gdata.connected || localSecret !== "") {
            setVerifying(false);
            return;
        }

        let stored = "";

        try {
            stored = sessionStorage.ls && sessionStorage.ls !== "" ? atob(sessionStorage.ls) : "";
        } catch (e) { /* */ }

        if (!stored) {
            setVerifying(false);
            return;
        }

        // One id for the whole ramp, with the password it is testing recorded
        // against it. The send path reads it back from there, so an unverified
        // password no longer has to be published to window._g to reach the
        // orchestrator - which is what made a reply answerable by the wrong
        // password in the first place.
        const probeId = rememberProbe(newProbeId(), stored);

        // Same retry as the status query above, and for the same reason: asked
        // once at the instant of connection this went unanswered, leaving a
        // stored password unverified and the app locked with no way forward.
        //
        // It has to be stopped explicitly on a rejection. The old comment here
        // claimed the next pass "finds nothing and stops" - it does not: `stored`
        // is captured in this closure and neither dependency changes when a
        // password is rejected, so a wrong stored password was re-probed every
        // 2.5s for the life of the page. The damage was not the traffic: once the
        // user typed the *correct* password, a stale probe still in flight came
        // back "Decryption failure" and re-locked the app, deleting the
        // sessionStorage entry that had just been written successfully.
        const ask = () => wPostMessage({
            "type": "get_secrets",
            "id": probeId,
            "store_password": stored,
        });

        // Hold the dialog back while this is in flight. On a reconnect the stored
        // password answers in a few hundred ms, and prompting first made the
        // dialog flash up and vanish on every orchestrator restart. If no answer
        // comes the hold expires and the prompt appears as it did before, with
        // the retries still running behind it.
        setVerifying(true);
        const release = setTimeout(() => setVerifying(false), 1200);

        const stopAsking = askOnRamp(ask);

        stopStoredProbeRef.current = stopAsking;

        return () => {
            clearTimeout(release);
            stopAsking();
            stopStoredProbeRef.current = null;
        };
    // eslint-disable-next-line
    }, [gdata.connected, localSecret]);

    // The store is unlocked against a particular orchestrator process, not for
    // the lifetime of the tab. Drop the unlocked state when the connection goes:
    // on reconnect verifyStoredSecret re-checks the stored password, and if there
    // is none - it was cleared, or this is a different orchestrator with a
    // different store - the dialog asks for it rather than the app carrying on as
    // though the store were still open.
    useEffect(() => {
        if (gdata.connected) {
            return;
        }

        setLocalSecret("");
        setLocalSecretError("");
        setVerifying(false);

        try {
            window._g = window._g || {};
            window._g.localSecret = "";
        } catch (e) { /* */ }
    }, [gdata.connected]);

    // Ask again on every connection, not just once at mount. initDone only turns
    // true when a status reply arrives, and the mount-time question goes
    // unanswered whenever the page is open while the orchestrator is down. It
    // then stayed false for the life of the component, so starting the
    // orchestrator later left the dialog unable to open at all - the scan panel
    // sat there with its contents hidden and no way to unlock. Leaving the page
    // and coming back "fixed" it only because that remounted and asked again.
    useEffect(() => {
        if (!gdata.connected || initDone) {
            return;
        }

        // Keep asking until it answers. gdata.connected turns true the moment the
        // socket opens, and a question posted in that same tick goes nowhere -
        // the mount path has always waited 200ms for the same reason. One
        // unanswered question left initDone false for good, and with it a scan
        // panel with hidden contents and no dialog to unlock from.
        const ask = () => wPostMessage({
            "type": "get_status",
            "id": "51a10055-0000-0000-0000-000000000001"
        });

        return askOnRamp(ask);
    }, [gdata.connected, initDone]);

    useEffect(() => {
        let orchestratorStatus = {
            "type": "get_status",
            "id": "51a10055-0000-0000-0000-000000000001"
        };

        setTimeout(() => {
            wPostMessage(orchestratorStatus);
        }, 200 );

        function processIncomming(event) {
          try {
            let eventData = {
              ...(typeof event.data !== 'undefined') ? JSON.parse(event.data) : {},
            };

            // Reply to the store creation above. Unlock only now, once the store
            // really exists, and write the scanner config - which is what the
            // "Scanner configuration was not set!" answer was waiting for.
            if (eventData.id == "cf121212-1212-1212-1212-000000000000") {
                setSecretInitDone(true);

                if (eventData.error) {
                    setLocalSecretError(`${eventData.error}`);
                } else {
                    // The password this reply answers for - same reasoning as the
                    // 420005ec handler below.
                    const created = probedPassword(eventData.id);

                    if (created) {
                        holdVerifiedSecret(created);

                        props.setLocalSecret(created);
                        setLocalSecret(created);
                        setLocalSecretError("");

                        try { sessionStorage.ls = btoa(created); } catch (e) { /* */ }

                        setTheConfig(getApiUrl(poolData.envURL), poolData);

                        // refresh oConfig so the dialog does not think it is still
                        // in first-time mode
                        wPostMessage({
                            "type": "get_status",
                            "id": "51a10055-0000-0000-0000-000000000001"
                        });
                    }
                }
            }
                
            // Reply to the get_secrets check sent from validatePassword: this is
            // the only thing that proves the typed password opens the store. A
            // rejection comes back untyped with an error; a success comes back as
            // the secrets payload itself.
            // A decryption failure on any request means the held password does not
            // open the store, so put this dialog back up rather than leaving the
            // app looking unlocked.
            if (`${eventData.error}`.startsWith("Decryption failure")) {
                // Stop the stored-password ramp: the password it is testing has
                // just been rejected, so every further attempt is another
                // rejection waiting to re-lock a store the user may since have
                // opened correctly.
                stopStoredProbe();

                setLocalSecret("");
                setLocalSecretError(eventData.error);

                clearStoredSecret();
            }

            if (`${eventData.id}`.startsWith("420005ec")) {
                if (`${eventData.error}`.startsWith("Decryption failure")) {
                    // handled above
                } else if (eventData.type == "secrets") {
                    // The password this reply answers for, not whatever is on the
                    // global now. Reading the global back unlocked with a password
                    // that had never been verified whenever a stored-password
                    // check outlived the verifying hold and the user had typed a
                    // different one in the meantime.
                    const verified = probedPassword(eventData.id);

                    if (verified) {
                        holdVerifiedSecret(verified);

                        props.setLocalSecret(verified);
                        setLocalSecret(verified);
                        setLocalSecretError("");

                        try { sessionStorage.ls = btoa(verified); } catch (e) { /* */ }
                    }
                }
            }

            if (eventData.id == "51a10055-0000-0000-0000-000000000001" && eventData.type=="status") {
                const {scanner_config_set,secret_store_exists} = eventData;
                setInitDone(true);

                setOConfig({
                    config:scanner_config_set,
                    store:secret_store_exists
                });
            }
          } catch (e) { /* */ }
        }

        window.addEventListener("message", processIncomming, false);

        return () => {
          window.removeEventListener("message", processIncomming, false);
        }
      }, []);

    const validatePassword = (first, valA, valB) => {
        const _NO_EMPTY_PASS = "Password cannot be empty.";

        if (first) {
            if (`${valA.value}` != `${valB.value}`) {
                setLocalSecretError("Password do not match.");

                return;
            }

            if (`${valA.value}`== "" || `${valB.value}` == "") {
                setLocalSecretError(_NO_EMPTY_PASS);

                return;
            }
        }

        if (`${valA.value}` == "") {
            setLocalSecretError(_NO_EMPTY_PASS);

            return;
        }
        
        // Not written to window._g here: it has not been verified yet. Both
        // branches below record it against the id of the request that tests it,
        // and the reply handler promotes it only once the orchestrator accepts.

        // `first` is what the caller validated against just above; read that rather
        // than the state, so the branch taken always matches the checks performed.
        if (first) {
            // Create the store with this password, and wait for the orchestrator to
            // confirm before treating it as open. This used to set the password
            // locally and call window.location.reload() in the same tick, while the
            // add_secret that actually creates the store was left to an effect keyed
            // on localSecret - so creation raced the navigation and frequently never
            // went out, leaving the store non-existent and the config unwritten.
            setLocalSecretError("");

            rememberProbe("cf121212-1212-1212-1212-000000000000", valA.value);

            wPostMessage({
                "type": "add_secret",
                "id": "cf121212-1212-1212-1212-000000000000",
                "secret_key": '__INIT__',
                "store_password": valA.value,
                "secret": {
                    "type": 'username_password',
                    "user_name": 'x',
                    "password": `x`
                }
            });

            return;
        }

        // Unlocking an existing store: stay locked until the orchestrator confirms
        // the password actually decrypts it. The get_secrets call below is that
        // check, and its reply is handled in processIncomming. Treating the store
        // as open here let a wrong password dismiss this dialog and reveal the
        // scan UI, and persisted that wrong password to sessionStorage, so the
        // next page load skipped the prompt entirely and every secret operation
        // failed with "Decryption failure - possibly incorrect password".

        // Stop any stored-password ramp still running before testing this one.
        // Two checks in flight against one mutable global is what let the wrong
        // reply answer for the wrong password.
        stopStoredProbe();

        const typedProbeId = rememberProbe(newProbeId(), valA.value);

        setTimeout(() => {
            wPostMessage({
                "type": "get_secrets",
                "id": typedProbeId,
                "store_password": valA.value,
            });
        }, 1000);
    }

    if (!isPrimaryInstance) {
        return null;
    }

    return (
        <Dialog
            open={localSecret == "" && gdata.connected && initDone && !verifying}
            keepMounted
            slotProps={{
                paper: {
                    sx: {
                        // border:"1px solid red !important",
                        background:"transparent !important",
                        width: 284,
                        padding: "80px 145px 80px 145px",
                    }
                }
            }}
            sx= {{
                background:"rgba(0,0,0,0.7)",
                overflow: "visible",
            }}


            onClose={() => {}}
            aria-describedby="sdescription"
        >
            {/* <DialogLogo /> */}
            <DialogTitle
                style={{
                    zIndex:4,
                    fontFamily: "Inter",
                    padding: 0,
                    fontSize: 18,
                    // MUI's DialogTitle is text-align: start, so the heading sat left
                    // of the description, field and button, which all centre.
                    textAlign: "center",
                    color: "var(--scan-enter-password-title-text-color)"
                }}
            >
                Enter secret store password
            </DialogTitle>

            <DialogContent
                style={{
                    paddingTop: 0,
                    zIndex:2,
                    overflow: "visible",
                }}
            >
                
                <div style={{
                //   filter: "url(#vignette-filter)",
                
                  // Same panel, same size and position as before - anchored to the
                  // dialog paper rather than offset from its static position, and
                  // with the fixed 380px height replaced by a floor. The height was
                  // pinned, so the first-time variant (which adds Confirm Password)
                  // grew past it and the actions row was painted outside the panel.
                  // minHeight keeps the usual single-field dialog looking exactly as
                  // it did; the panel only stretches when the content needs it.
                  position: "absolute",
                  zIndex: -1,
                  inset: 0,
                  overflow: "visible",
                }}>
                    {isDarkMode? <MainBg2 isDarkMode={isDarkMode} stretch></MainBg2>:
                  <MainBg isDarkMode={isDarkMode} stretch></MainBg>}
                  </div>

                <DialogContentText
                    id="sdescription"
                    style={{
                        zIndex:2,
                        fontFamily: "InterLight",
                        fontSize: 12,
                        lineHeight: "22px!important",
                        color: "rgba(255, 255, 255, 1)",
                        marginTop: 10
                    }}
                >
                    Secret store password is used to securely store keys and passwords.
                </DialogContentText>

                {/* Without this the bare form submits natively on Enter, which
                    navigates and throws away the unlock in progress - pressing
                    Enter simply appeared to do nothing. */}
                <form onSubmit={(e) => { e.preventDefault(); }}>
                    <Box
                        className="noScroll"
                        sx={{
                            marginTop: "20px",
                            overflow: "scroll"
                        }}
                    >
                        {firstTimePassword ?
                            <div
                                style={{
                                    fontFamily: 'InterLight',
                                    fontSize: 12,
                                    lineHeight: "15px",
                                    color: "#FFFFFF",
                                    opacity: 0.8,
                                    marginBottom: "4px",
                                    marginLeft: "10px"
                                }}
                            >
                                Password
                            </div>
                            :
                            <></>
                        }

                        <input
                            ref={lsPass}
                            id="lsPass"
                            type="password"
                            style={{
                                width: "calc(100% - 20px)",
                                background: "rgba(1, 1, 1, 0.3)",
                                color: "#FFFFFF",
                                opacity: 0.8,
                                fontSize: 24,
                                outline: "none",
                                borderRadius: 4,
                                border: "none",
                                padding: "3px 10px"
                            }}
                            tabIndex={0}
                            autoComplete="one-time-code"
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    validatePassword(firstTimePassword, lsPass.current, lsPass2.current);
                                }
                            }}
                        />

                        {firstTimePassword ?
                            <div style={{ widdth: "100%", marginTop: "10px" }}>
                                <div
                                    style={{
                                        fontFamily: 'InterLight',
                                        fontSize: 12,
                                        lineHeight: "15px",
                                        color: "#FFFFFF",
                                        opacity: 0.8,
                                        marginBottom: "4px",
                                        marginLeft: "10px"
                                    }}
                                >
                                    Confirm Password
                                </div>

                                <input
                                    ref={lsPass2}
                                    id="lsPass2"
                                    type="password"
                                    style={{
                                        width: "calc(100% - 20px)",
                                        background: "rgba(1, 1, 1, 0.3)",
                                        marginTop: firstTimePassword ? 0 : 0,
                                        // Must match the password field above: type="password"
                                        // scales its mask dots with the font size, so 26 here
                                        // rendered visibly bigger dots than the 24 up there.
                                        fontSize: 24,
                                        color: "#FFFFFF",
                                        opacity: 0.8,
                                        outline: "none",
                                        borderRadius: 4,
                                        border: "none",
                                        padding: "3px 10px"
                                    }}
                                    autoComplete="one-time-code"
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            validatePassword(firstTimePassword, lsPass.current, lsPass2.current);
                                        }
                                    }}
                                />
                            </div>
                            :<></>
                        }
                    </Box>
                </form>

                {localSecretError != "" ?
                    <div
                        style={{
                            color: "var(--scan-enter-password-input-error-text-color)",
                            marginTop: 10,
                            fontSize: "14px",
                            background: "var(--scan-enter-password-input-error-bg-color)",
                            padding: 10,
                            borderRadius: 4
                        }
                    }>
                        {localSecretError}
                    </div>
                    :
                    ""
                }
            </DialogContent>

            <DialogActions
                style={{
                    zIndex:4,
                    textAlign: "center",
                    justifyContent: "center",
                    padding: 0,
                    marginTop: 30,
                    // The panel now sizes to the dialog, so the content no longer
                    // needs hand-offsetting to sit under it: title, fields and
                    // button all centre on the same axis.
                }}
            >
                <button
                    style={{
                        maxWidth: "200px",
                    }}
                    className="enter-secret-password__button"
                    onClick={() => {
                        validatePassword(firstTimePassword, lsPass.current, lsPass2.current);
                    }}
                >
                    {firstTimePassword ? "Submit password" : "Ok"}
                </button>
            </DialogActions>
        </Dialog>
    )
}
